// src/components/Navbar.tsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeSwitcher from "./ThemeSwitcher";
import { C, FONT } from "../theme";
import { roleNavigation } from "../utils/roleNavigation";

// ── Role type ─────────────────────────────────────────────────────────────────
type UserRole = "admin" | "coach" | "ranking_admin";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}


const Navbar: React.FC = () => {
  const { currentUser, userRole, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [loggingOut, setLoggingOut] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const w = useWindowWidth();
  const isMobile = w < 768;

  // Cast to typed role for explicit comparisons
  const role = userRole as UserRole | null;

  // Both admin and ranking_admin use the two-row admin navbar layout
  const isAdmin = role === "admin" || role === "ranking_admin";

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      console.error("Logout failed:", err);
      setLoggingOut(false);
    }
  };

  // ── Full admin link list (9 links — shown to "admin" only) ────────────────
  const adminLinks = roleNavigation("admin").map((item) => ({ label: t(item.labelKey), path: item.path }));

  // ── Restricted link list (5 links — shown to "ranking_admin" only) ────────
  const rankingAdminLinks = roleNavigation("ranking_admin").map((item) => ({ label: t(item.labelKey), path: item.path }));

  const coachLinks = roleNavigation("coach").map((item) => ({ label: t(item.labelKey), path: item.path }));

  // Decide which link array to render in the admin nav rows
  const activeAdminLinks = role === "ranking_admin" ? rankingAdminLinks : adminLinks;

  // Links used in the mobile drawer
  const links = isAdmin ? activeAdminLinks : coachLinks;

  const isActive = (path: string) =>
    path === "/admin"
      ? location.pathname === "/admin"
      : location.pathname.startsWith(path);

  // ── Desktop admin: two-row layout ─────────────────────────────────────────
  if (!isMobile && isAdmin && currentUser) {
    return (
      <nav style={s.navAdmin}>
        {/* Row 1 — brand + user area */}
        <div style={s.adminRow1}>
          <Link to="/admin" style={s.brand}>
            <img src="/geo.png" alt="Logo" style={s.logo} />
            <div>
              <div style={s.brandTitle}>TAEKWONDO</div>
              <div style={s.brandSub}>Georgian National Federation</div>
            </div>
          </Link>

          <div style={s.userArea}>
            <ThemeSwitcher compact />
            <LanguageSwitcher />
            <div style={s.userInfo}>
              <div style={s.userRole}>
                {role === "ranking_admin" ? t("nav.role.rankingAdmin") : t("nav.role.admin")}
              </div>
              <div style={s.userEmail}>{currentUser.email}</div>
            </div>
            <button style={s.logoutBtn} onClick={handleLogout} disabled={loggingOut}>
              {loggingOut ? "..." : t("nav.logout")}
            </button>
          </div>
        </div>

        {/* Row 2 — nav links (filtered per role) */}
        <div style={s.adminRow2}>
          {activeAdminLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              style={{ ...s.adminLink, ...(isActive(link.path) ? s.adminLinkActive : {}) }}
            >
              {link.label}
              {isActive(link.path) && <div style={s.linkUnderline} />}
            </Link>
          ))}
        </div>
      </nav>
    );
  }

  // ── Desktop coach: single-row layout ──────────────────────────────────────
  if (!isMobile && !isAdmin && currentUser) {
    return (
      <nav style={s.nav}>
        <Link to="/dashboard" style={s.brand}>
          <img src="/geo.png" alt="Logo" style={s.logo} />
          <div>
            <div style={s.brandTitle}>TAEKWONDO</div>
            <div style={s.brandSub}>Georgian National Federation</div>
          </div>
        </Link>

        <div style={s.links}>
          {coachLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              style={{ ...s.link, ...(isActive(link.path) ? s.linkActive : {}) }}
            >
              {link.label}
              {isActive(link.path) && <div style={s.linkUnderline} />}
            </Link>
          ))}
        </div>

        <div style={s.userArea}>
          <ThemeSwitcher compact />
          <LanguageSwitcher />
          <div style={s.userInfo}>
            <div style={s.userRole}>{t("nav.role.coach")}</div>
            <div style={s.userEmail}>{currentUser.email}</div>
          </div>
          <button style={s.logoutBtn} onClick={handleLogout} disabled={loggingOut}>
            {loggingOut ? "..." : t("nav.logout")}
          </button>
        </div>
      </nav>
    );
  }

  // ── No user logged in — minimal nav ────────────────────────────────────────
  if (!currentUser && !isMobile) {
    return (
      <nav style={s.nav}>
        <Link to="/" style={s.brand}>
          <img src="/geo.png" alt="Logo" style={s.logo} />
          <div>
            <div style={s.brandTitle}>TAEKWONDO</div>
            <div style={s.brandSub}>Georgian National Federation</div>
          </div>
        </Link>
      </nav>
    );
  }

  // ── Mobile layout (all roles) ──────────────────────────────────────────────
  return (
    <>
      <nav style={{ ...s.nav, padding: "0 16px" }}>
        <Link to={isAdmin ? "/admin" : "/dashboard"} style={s.brand}>
          <img src="/geo.png" alt="Logo" style={s.logo} />
        </Link>

        {currentUser && (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
            <ThemeSwitcher compact />
            <LanguageSwitcher />
            <button
              style={s.hamburger}
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Menu"
            >
              <span style={{ ...s.hamburgerLine, transform: menuOpen ? "rotate(45deg) translate(4px, 4px)" : "none" }} />
              <span style={{ ...s.hamburgerLine, opacity: menuOpen ? 0 : 1 }} />
              <span style={{ ...s.hamburgerLine, transform: menuOpen ? "rotate(-45deg) translate(4px, -4px)" : "none" }} />
            </button>
          </div>
        )}
      </nav>

      {/* Mobile drawer */}
      {menuOpen && currentUser && (
        <div style={s.mobileDrawer}>
          <div style={s.mobileUserRow}>
            <div style={s.userRole}>
              {role === "ranking_admin"
                ? t("nav.role.rankingAdmin")
                : isAdmin
                ? t("nav.role.admin")
                : t("nav.role.coach")}
            </div>
            <div style={{ ...s.userEmail, fontSize: "13px" }}>{currentUser.email}</div>
          </div>
          {links.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              style={{ ...s.mobileLink, ...(isActive(link.path) ? s.mobileLinkActive : {}) }}
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          <button
            style={s.mobileLogoutBtn}
            onClick={() => { setMenuOpen(false); handleLogout(); }}
            disabled={loggingOut}
          >
            {loggingOut ? "..." : t("nav.logout")}
          </button>
        </div>
      )}
    </>
  );
};

const s: Record<string, React.CSSProperties> = {
  // ── Single-row nav (coach desktop / fallback) ─────────────────────────────
  nav: {
    backgroundColor: C.surface,
    borderBottom: `1px solid ${C.border}`,
    height: "60px",
    display: "flex",
    alignItems: "center",
    padding: "0 28px",
    gap: "32px",
    position: "sticky",
    top: 0,
    zIndex: 100,
  },

  // ── Two-row nav (admin desktop) ───────────────────────────────────────────
  navAdmin: {
    backgroundColor: C.surface,
    borderBottom: `1px solid ${C.border}`,
    display: "flex",
    flexDirection: "column",
    position: "sticky",
    top: 0,
    zIndex: 100,
  },
  adminRow1: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 28px",
    height: "56px",
    borderBottom: `1px solid ${C.border}`,
  },
  adminRow2: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    padding: "0 20px",
    height: "40px",
    backgroundColor: C.surface2,
    overflowX: "auto",
  },
  adminLink: {
    position: "relative",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: 500,
    color: C.text2,
    textDecoration: "none",
    borderRadius: "5px",
    transition: "color 0.15s",
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
  },
  adminLinkActive: {
    color: C.red,
    fontWeight: 700,
    backgroundColor: C.redDim,
  },

  // ── Shared elements ───────────────────────────────────────────────────────
  brand: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    textDecoration: "none",
  },
  logo: {
    height: "36px",
    width: "auto",
    objectFit: "contain",
  },
  brandTitle: {
    fontFamily: FONT.display,
    fontSize: "17px",
    letterSpacing: "2px",
    color: C.text,
    lineHeight: 1,
  },
  brandSub: {
    fontSize: "9px",
    color: C.text2,
    letterSpacing: "1px",
    textTransform: "uppercase",
    marginTop: "1px",
  },
  links: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flex: 1,
  },
  link: {
    position: "relative",
    padding: "6px 12px",
    fontSize: "13px",
    fontWeight: 500,
    color: C.text2,
    textDecoration: "none",
    borderRadius: "6px",
    transition: "color 0.15s",
    letterSpacing: "0.3px",
  },
  linkActive: {
    color: C.text,
    backgroundColor: C.surface2,
  },
  linkUnderline: {
    position: "absolute",
    bottom: "2px",
    left: "10px",
    right: "10px",
    height: "2px",
    backgroundColor: C.red,
    borderRadius: "2px",
  },
  userArea: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },
  userInfo: {
    textAlign: "right",
  },
  userRole: {
    fontSize: "9px",
    fontWeight: 700,
    letterSpacing: "1.5px",
    color: C.red,
  },
  userEmail: {
    fontSize: "12px",
    color: C.text2,
    marginTop: "1px",
  },
  logoutBtn: {
    backgroundColor: "transparent",
    border: `1px solid ${C.border2}`,
    borderRadius: "8px",
    color: C.text2,
    fontSize: "12px",
    fontWeight: 500,
    padding: "5px 12px",
    cursor: "pointer",
    letterSpacing: "0.3px",
  },

  // ── Mobile ────────────────────────────────────────────────────────────────
  hamburger: {
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    gap: "4px",
    width: "36px",
    height: "36px",
    background: "none",
    border: `1px solid ${C.border}`,
    borderRadius: "8px",
    cursor: "pointer",
    padding: "6px 8px",
  },
  hamburgerLine: {
    display: "block",
    height: "2px",
    backgroundColor: C.text,
    borderRadius: "2px",
    transition: "transform 0.2s, opacity 0.2s",
  },
  mobileDrawer: {
    position: "sticky" as const,
    top: "60px",
    zIndex: 99,
    backgroundColor: C.surface,
    borderBottom: `1px solid ${C.border}`,
    display: "flex",
    flexDirection: "column" as const,
    padding: "12px 16px 16px",
    gap: "2px",
    boxShadow: C.shadow,
  },
  mobileUserRow: {
    padding: "8px 12px 12px",
    borderBottom: `1px solid ${C.border}`,
    marginBottom: "8px",
  },
  mobileLink: {
    padding: "11px 12px",
    fontSize: "14px",
    fontWeight: 500,
    color: C.text2,
    textDecoration: "none",
    borderRadius: "8px",
    display: "block",
  },
  mobileLinkActive: {
    backgroundColor: C.surface2,
    color: C.text,
    fontWeight: 700,
    borderLeft: `3px solid ${C.red}`,
    paddingLeft: "10px",
  },
  mobileLogoutBtn: {
    marginTop: "8px",
    padding: "10px 12px",
    fontSize: "13px",
    fontWeight: 600,
    color: C.red,
    backgroundColor: C.redDim,
    border: `1px solid ${C.red}`,
    borderRadius: "8px",
    cursor: "pointer",
    textAlign: "left" as const,
  },
};

export default Navbar;
